/**
 * WP4 — "what do I get", answered before the paywall's "what does it cost".
 * Ported from student-client's PlanCompareStep, and bound by the same two
 * copy rules:
 *   1. NO FREE-TIER NUMBERS. The real limits are server-tunable
 *      `feature_rules` data, retuned during beta with no app release —
 *      "Limited" is the honest word, not a quota this file would go stale on.
 *   2. ONLY CLAIM WHAT'S ENFORCED. Every row maps to a live gate.
 *
 * Row 1 says "Follow", never "Link" — parent_child_linking IS enforced
 * (billing-server's checkStudentSeatAddition, called from pairing.ts), but a
 * parent with no household yet — every fresh signup, which is this screen's
 * whole audience — passes its grandfather path unrestricted. A lapsed
 * household would fail that same claim under "Link", so the word has to stay
 * accurate to the feature, not just to this one audience.
 *
 * The mount-time skip reuses isSubscribed from lib/plans — the exact
 * predicate PaywallPage itself starts on — so the two screens can never
 * disagree about who already has a plan. A failed getAccess falls through to
 * showing the pitch rather than trapping the parent on a blank screen.
 */

import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { IconX, IconCheck } from '@tabler/icons-react';
import { getAccess } from '@/lib/api';
import { isSubscribed, TRIAL_DAYS } from '@/lib/plans';
import { afterPlanPath } from '@/lib/invite-flow';
import pricingPlan from '@tupai/tokens/assets/scenes/pricing-plan.svg';
import { useTranslation } from '@tupai/i18n/react';

export const Route = createFileRoute('/_protected/onboarding/plans')({
  component: OnboardingPlansPage,
});

const ROWS: {
  key: 'rowFollow' | 'rowReports' | 'rowAssign' | 'rowPapers';
  free: 'yes' | 'no' | 'limited';
}[] = [
  { key: 'rowFollow', free: 'yes' },
  { key: 'rowReports', free: 'no' },
  { key: 'rowAssign', free: 'no' },
  { key: 'rowPapers', free: 'limited' },
];

function OnboardingPlansPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [ready, setReady] = useState(false);
  const { t } = useTranslation('auth');

  // Reached from Profile's "Upgrade now" as well as from onboarding, so the
  // exit returns where it came from. Defaults to /children, which is both the
  // onboarding destination and a safe fallback.
  const fromState = location.state.from;
  const exitTo = fromState ?? afterPlanPath();

  useEffect(() => {
    let cancelled = false;
    getAccess()
      .then((access) => {
        if (cancelled) return;
        if (isSubscribed(access)) {
          navigate({ to: exitTo, replace: true });
        } else {
          setReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate, exitTo]);

  if (!ready) return null;

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-surface">
      {/* X exits to wherever this was opened from — the free tier is a real
          destination, not a dead end. It shares the hero's primary tint stage,
          matching the details form: DESIGN.md keeps `surface`
          as the page background, and the tint is what stops three consecutive
          onboarding screens reading as blank sheets. Square-edged, so the
          stage reads as a field rather than a card. */}
      <div className="safe-area-top shrink-0 bg-primary/10 pb-6">
        <div className="flex items-center px-3 pt-2">
          <button
            aria-label={t('close')}
            onClick={() => navigate({ to: exitTo, replace: true })}
            className="flex size-11 cursor-pointer items-center justify-center rounded-pill text-content transition-colors hover:bg-surface/60"
          >
            <IconX size={20} stroke={2.5} />
          </button>
        </div>
        <img src={pricingPlan} alt="" className="mx-auto h-auto w-32" />
        {/* Only Family Solo has a trial (lib/plans.ts). */}
        <h1 className="font-display mt-3 px-6 text-center text-2xl text-content">
          {t('freeTrialHeading', { days: TRIAL_DAYS })}
        </h1>
        <p className="mt-1 px-6 text-center text-base text-muted">
          {t('soloTrialPrefix')}{' '}
          <span className="font-bold text-primary">{t('soloTrialDays', { days: TRIAL_DAYS })}</span>
          .
        </p>
      </div>

      <div className="flex flex-1 flex-col overflow-y-auto px-6 pb-6 pt-2">
        <div className="flex flex-1 flex-col md:flex-none md:my-auto">
          {/* The Premium column is a continuous tinted band behind the whole
              table, so its header needs the same top padding the rows have —
              without it the label sat flush on the band's top edge. Column
              widths stay narrow, which fits every English row label on one line;
              the longest BM label wraps to two and the row grows to suit, which
              is why the label cell has no fixed height. */}
          <table className="mt-5 w-full text-sm">
            <thead>
              <tr>
                <th />
                <th className="w-14 py-3 text-sm font-bold text-muted">{t('colFree')}</th>
                <th className="w-16 rounded-t-lg bg-primary/10 py-3 text-sm font-bold text-content">
                  {t('colSolo')}
                </th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row, i) => (
                <tr key={row.key}>
                  <td className="py-3.5 pr-3 leading-snug text-content">{t(row.key)}</td>
                  <td className="py-3.5 text-center">
                    {row.free === 'yes' ? (
                      <IconCheck
                        size={18}
                        className="mx-auto block text-primary"
                        aria-label={t('included')}
                      />
                    ) : row.free === 'limited' ? (
                      <span className="block text-sm font-bold text-muted">{t('limited')}</span>
                    ) : (
                      <IconX
                        size={18}
                        className="mx-auto block text-hint"
                        aria-label={t('notIncluded')}
                      />
                    )}
                  </td>
                  <td
                    className={`bg-primary/10 py-3.5 text-center ${
                      i === ROWS.length - 1 ? 'rounded-b-lg' : ''
                    }`}
                  >
                    <IconCheck
                      size={18}
                      className="mx-auto block text-primary"
                      aria-label={t('included')}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="mt-4 text-sm leading-relaxed text-muted">{t('freeStaysFree')}</p>

          <div className="mt-auto flex flex-col gap-2 pt-6 md:mt-0">
            <button
              onClick={() =>
                navigate({
                  to: '/paywall',
                  replace: Boolean(fromState),
                  state: { trigger: 'onboarding', from: fromState },
                })
              }
              className="cta-primary"
            >
              {t('startFreeTrial')}
            </button>
            <div className="flex min-h-20 flex-col gap-2">
              <button
                onClick={() => navigate({ to: exitTo, replace: true })}
                className="cta-secondary"
              >
                {t('continueWithFree')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
